import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ConfirmDialogComponent, Filter, Logger } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { AssetDomain, Asset, AssetsDomainService, AssetsService, AzureAssetsSearchService, DropdownFacetItem } from '../..';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const log = new Logger('AssetsComponent');

@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrls: ['./assets.component.scss']
})

export class AssetsComponent<T extends Asset> implements OnInit {

  @ViewChild(MatSidenav) filterSidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  @ViewChild('assetsTable', { static: false }) assetsTable: any;
  @ViewChild('assetsCards', { static: false }) assetsCards: any;

  fileTypes: DropdownFacetItem[] = [];
  assetGroups: DropdownFacetItem[] = [];
  filters: Filter[] = [];

  viewModel$ = combineLatest([
    this.assetsService.isLoading$,
    this.assetsService.viewMode$,
    this.azureAssetsSearchService.assetGroups$,
    this.azureAssetsSearchService.fileTypes$
  ]).pipe(
    map(([isLoading, viewMode, assetGroups, fileTypes]) => {
      this.fileTypes = fileTypes;
      this.assetGroups = assetGroups;

      return { isLoading, viewMode, assetGroups, fileTypes };
    })
  );

  filtersForm = new FormGroup({
    search: new FormControl(),
    assetGroups: new FormControl<DropdownFacetItem[]>([]),
    fileTypes: new FormControl<DropdownFacetItem[]>([]),
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
  });

  assetsFilters: string[] = [];
  rangeInvalid = false;
  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private matSnackBar: MatSnackBar,
    private assetsService: AssetsService<Asset>,
    private assetDomainService: AssetsDomainService<AssetDomain>,
    private azureAssetsSearchService: AzureAssetsSearchService,
  ) { }

  async ngOnInit(): Promise<void> {
    log.debug('init');
    await this.azureAssetsSearchService.init();
    this.azureAssetsSearchService.clearNavigationContextAndCache();

    const hasFilter = this.route.snapshot.queryParams['filter'];
    if (hasFilter) {
      const savedFilters = this.azureAssetsSearchService.getFilters();

      if (savedFilters) {
        this.filtersForm.patchValue(savedFilters);
        this.onFiltersApply();
      } else {
        this.router.navigate([], { relativeTo: this.route, queryParams: { filter: null }, queryParamsHandling: 'merge' });
        this.azureAssetsSearchService.search('');
      }
    } else {
      this.azureAssetsSearchService.clearFilters();
      this.azureAssetsSearchService.reset();
      this.azureAssetsSearchService.search('');
    }

    this.setupSearchListener();
  }

  ngOnDestroy(): void {
    if (this.route.snapshot.queryParams['filter'] && this.route.snapshot.queryParams['filter'] == 'true') {
      this.azureAssetsSearchService.setFilters(this.filtersForm.value);
    } else {
      this.azureAssetsSearchService.clearFilters();
      this.azureAssetsSearchService.reset();
    }
  }

  private setupSearchListener(): void {
    this.filtersForm.controls.search.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        if (this.filterSidenav?.opened) {
          this.filterSidenav.close();
        }

        if (value) {
          this.router.navigate([], { relativeTo: this.route, queryParams: { filter: 'true' }, queryParamsHandling: 'merge' });
        }

        this.clearSelection();
        this.filters = this.buildFilters(this.filtersForm.value);
        this.azureAssetsSearchService.search(value ?? '');
      });
  }

  compareWithIdFn = (o1: any, o2: any) => {
    return o1 && o2 ? o1.value === o2.value : o1 === o2;
  };

  buildFilters(formValue: any): Filter[] {
    const filters: Filter[] = [];

    // Search
    if (formValue.search) {
      filters.push({
        fieldName: 'search',
        value: formValue.search,
        displayText: `${formValue.search}`
      });
    }

    // Asset Groups
    formValue.assetGroups?.forEach((item: DropdownFacetItem) => {
      filters.push({
        fieldName: 'assetGroups',
        value: item.value.toString(),
        displayText: item.value.toString()
      });
    });

    // File Types
    formValue.fileTypes?.forEach((item: DropdownFacetItem) => {
      filters.push({
        fieldName: 'fileTypes',
        value: item.value.toString(),
        displayText: item.value.toString()
      });
    });

    // Upload Start Date (separate chip)
    if (formValue.startDate) {
      filters.push({
        fieldName: 'startDate',
        value: formValue.startDate.toString(),
        displayText: `Upload Start Date: ${this.formatDate(formValue.startDate)}`
      });
    }

    // Upload End Date (separate chip)
    if (formValue.endDate) {
      filters.push({
        fieldName: 'endDate',
        value: formValue.endDate.toString(),
        displayText: `Upload End Date: ${this.formatDate(formValue.endDate)}`
      });
    }

    return filters;
  }

  removeFilter(filter: Filter): void {
    switch (filter.fieldName) {
      case 'search':
        this.filtersForm.patchValue({ search: null });
        break;
      case 'assetGroups':
        this.removeFromArrayControl('assetGroups', filter.value);
        break;
      case 'fileTypes':
        this.removeFromArrayControl('fileTypes', filter.value);
        break;
      case 'startDate':
        this.filtersForm.patchValue({ startDate: null });
        break;
      case 'endDate':
        this.filtersForm.patchValue({ endDate: null });
        break;
    }

    this.onFiltersApply();
  }

  removeFromArrayControl(controlName: 'assetGroups' | 'fileTypes', valueToRemove: string): void {
    const current = this.filtersForm.value[controlName] ?? [];
    const updated = current.filter(
      (item: DropdownFacetItem) =>
        item.value.toString() !== valueToRemove
    );

    this.filtersForm.patchValue({
      [controlName]: updated
    });
  }

  clearFilters(): void {
    this.filtersForm.reset({
      search: null,
      assetGroups: [],
      fileTypes: [],
      startDate: null,
      endDate: null
    });
  }

  formatDate(date: Date | null): string {
    if (!date) return '';

    return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.filterSidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.filterSidenav.close();
  }

  onFiltersReset() {
    this.filterSidenav.close();
    this.clearFilters();
    this.onFiltersApply();
    this.router.navigate([], { relativeTo: this.route, queryParams: { filter: null }, queryParamsHandling: 'merge' });
  }

  onFiltersApply() {
    if (this.filterSidenav?.opened) {
      this.filterSidenav.close();
    }

    this.filters = this.buildFilters(this.filtersForm.value);

    let filters = {
      search: this.filtersForm.value.search ?? '',
      assetGroups: this.filtersForm.value.assetGroups ? this.filtersForm.value.assetGroups.map((item: DropdownFacetItem) => item) : [],
      fileType: this.filtersForm.value.fileTypes ? this.filtersForm.value.fileTypes.map((item: DropdownFacetItem) => item) : [],
      startDate: this.filtersForm.value.startDate ? this.formattedDatetoEpoch(this.filtersForm.value.startDate) : null,
      endDate: this.filtersForm.value.endDate ? this.formattedDatetoEpoch(this.filtersForm.value.endDate) : null
    }

    if (!filters.startDate) this.filtersForm.value.startDate = null;
    if (!filters.endDate) this.filtersForm.value.endDate = null;

    this.rangeInvalid = false;

    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      this.rangeInvalid = true;
      return
    }

    this.azureAssetsSearchService.updateSelectedItems('fileTypes', filters.fileType as DropdownFacetItem[]);
    this.azureAssetsSearchService.updateSelectedItems('assetGroups', filters.assetGroups as DropdownFacetItem[]);
    this.azureAssetsSearchService.updateDateRange('dateRange', filters.startDate, filters.endDate);
    this.azureAssetsSearchService.search(filters.search);

    this.router.navigate([], { relativeTo: this.route, queryParams: { filter: true }, queryParamsHandling: 'merge' });
  }

  formattedDatetoEpoch(dateObj: Date | null): number | null {
    if (!dateObj) return null;
    const dateString = new Date(dateObj).toLocaleDateString();
    const [month, day, year] = dateString.split('/').map(Number);
    // Format the date as "YYYY-MM-DDZ00:00:00"
    const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}Z00:00:00`;
    var epochDate = new Date(formattedDate);
    var epochVal = epochDate.getTime() / 1000.0;

    return epochVal > 0 ? epochVal : null;
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.clearSelection();
    this.azureAssetsSearchService.search('');
  }

  clearSelection() {
    if (this.assetsTable) {
      this.assetsTable.clearSelection();
    } else if (this.assetsCards) {
      this.assetsCards.clearSelection();
    }
  }

  toggleView(mode: string) {
    this.assetsService.toggleViewMode(mode);
  }

  getDistinctRecords(list: any) {
    const allAges = list.value;
    const uniqueSet = new Set(allAges)
    return [...uniqueSet]
  }

  deleteSelectedRecords() {
    let selectedRecords: any;
    if (this.assetsTable) {
      selectedRecords = this.assetsTable.getSelectedSectionRecords();
    } else if (this.assetsCards) {
      selectedRecords = this.assetsCards.getSelectedSectionRecords();
    }
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Asset?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            const assetIds = selectedRecords.map((record: any) => record.assetId);
            this.assetDomainService.deleteAssets(assetIds).subscribe({
              next: () => {
                this.matSnackBar.open(`Asset(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                this.clearSelection();
                this.onFiltersApply();
                if (this.assetsTable) {
                  this.assetsTable.clearSelection();
                } else if (this.assetsCards) {
                  this.assetsCards.clearSelection();
                }
              },
              error: (error) => {
                log.error('Error in deleting asset', error);

                if (error.error.value) {
                  throw new Error(error.error.value);
                } else {
                  throw new Error(error.message);
                }
              }
            });
          }
        });
    }
  }

  bulkUpdateAsDoNotUse() {
    let selectedRecords: any;
    if (this.assetsTable) {
      selectedRecords = this.assetsTable.getSelectedSectionRecords();
    } else if (this.assetsCards) {
      selectedRecords = this.assetsCards.getSelectedSectionRecords();
    }

    if (!selectedRecords || selectedRecords.length === 0) {
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Mark Do Not Use',
        message: 'Are you sure you want to mark the selected assets as Do Not Use?',
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(confirmResult => {
      if (!confirmResult) {
        return;
      }

      const assetIds = selectedRecords.map((record: any) => record.assetId);
      this.assetDomainService.bulkUpdateDoNotUse(assetIds).subscribe({
        next: () => {
          this.matSnackBar.open('Selected assets marked as Do Not Use successfully.', 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.clearSelection();
          this.onFiltersApply();
          this.assetsService.reload();
        },
        error: (error) => {
          log.error('Error updating assets as Do Not Use', error);
          const errorMessage = error?.error?.value || error?.message || 'Failed to mark selected assets as Do Not Use.';
          this.matSnackBar.open(errorMessage, 'OK', DEFAULT_SNACKBAR_CONFIG);
        }
      });
    });
  }

  downloadJSON() {
    this.assetsService.downloadAssetsJSON();
  }

  downloadCSV() {
    this.assetsService.downloadAssetsCSV();
  }

}
